import { useEffect } from 'react'

/**
 * Custom hook to sync autofilled form values to React state
 *
 * Browsers often autofill inputs without triggering onChange events,
 * causing controlled React inputs to be out of sync with the DOM.
 * This hook detects autofill via CSS animations and syncs the values.
 *
 * @param {Object} refs - Object mapping field names to React refs
 * @param {Function} syncCallback - Function to call with { fieldName: value } when autofill detected
 *
 * @example
 * const emailRef = useRef(null)
 * const passwordRef = useRef(null)
 *
 * useAutofillSync(
 *   { email: emailRef, password: passwordRef },
 *   (values) => {
 *     setFormData(prev => ({ ...prev, ...values }))
 *   }
 * )
 */
export default function useAutofillSync(refs, syncCallback) {
  useEffect(() => {
    // Check if we have valid refs and callback
    if (!refs || !syncCallback || typeof syncCallback !== 'function') {
      return
    }

    // Check for empty refs object
    if (Object.keys(refs).length === 0) {
      return
    }

    const inputElements = Object.entries(refs)
      .filter(([_, ref]) => ref?.current)
      .map(([name, ref]) => ({ name, element: ref.current }))

    if (inputElements.length === 0) {
      return
    }

    // Handler for autofill animation event
    const handleAutofill = (event, fieldName) => {
      // Small delay to ensure the browser has filled the value
      setTimeout(() => {
        const element = event.target
        const value = element.value

        // Only sync if there's actually a value
        if (value && value.trim()) {
          syncCallback({ [fieldName]: value })
        }
      }, 10)
    }

    // Attach animation listeners to detect autofill
    const listeners = inputElements.map(({ name, element }) => {
      const listener = (e) => handleAutofill(e, name)
      element.addEventListener('animationstart', listener, false)
      return { element, listener }
    })

    // Also check on mount for already-autofilled values
    const checkInitialValues = () => {
      const autofilled = {}
      inputElements.forEach(({ name, element }) => {
        // Check if the element has a value but state might not
        if (element.value && element.value.trim()) {
          autofilled[name] = element.value
        }
      })

      if (Object.keys(autofilled).length > 0) {
        syncCallback(autofilled)
      }
    }

    // Check immediately and after a short delay (for slower autofill)
    checkInitialValues()
    const timeoutId = setTimeout(checkInitialValues, 500)

    // Cleanup
    return () => {
      clearTimeout(timeoutId)
      listeners.forEach(({ element, listener }) => {
        element.removeEventListener('animationstart', listener, false)
      })
    }
  }, [refs, syncCallback])
}
